<template>
  <div class="relative bg-white py-8 pb-12 md:pb-20">
    <div class="w-full px-4 md:px-[5%] e1:m-auto e1:max-w-[1136px] e1:p-0">
      <div class="mb-8 md:flex md:gap-4">
        <!-- 左側: Overview テキスト -->
        <div class="md:flex-1">
          <div class="mb-4 font-Spectral [&_.rich-text>*:first-child]:pt-0 [&_.rich-text>*:last-child]:pb-0">
            <StyledRichText :html="experienceCms.overviewV2" />
          </div>
          <div v-if="overviewDrawerLink" class="mb-4 px-1 font-Spectral">
            <DrawerLinkButton variant="outline" :label="overviewDrawerLink.label" :on-click="onClickOverviewDrawerLink" />
          </div>
        </div>
        <!-- 右側: Location 情報 -->
        <div v-if="destinationImageUrl" class="mb-8 md:mb-0 md:w-[368px] md:flex-shrink-0">
          <div class="flex flex-col items-center">
            <OptimizedImage alt="Location" :class="['h-auto w-full object-cover md:max-w-[368px]']" :image-url="destinationImageUrl ?? ''" :optimized-image-options="{ width: 560 }" />
            <div v-if="experience.venueDetail.length > 0 || googleMapsUrl" class="mt-3 flex gap-x-4">
              <a v-if="experience.venueDetail.length > 0 || googleMapsUrl" class="cursor-pointer text-gold1 underline underline-offset-2" @click="openLocationDetailModal">Location Details</a>
              <a
                v-if="googleMapsUrl"
                aria-label="Open in Google Maps (opens in a new tab)"
                class="text-gold1 underline underline-offset-2"
                rel="noopener noreferrer"
                target="_blank"
                :href="googleMapsUrl"
                >Google Maps</a
              >
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="w-full px-4 md:px-[5%] e1:m-auto e1:max-w-[1136px] e1:p-0">
      <OrganismsPagesEnExperiencesDetailInfoBlock :experience="experience" />
    </div>
    <MoleculesWabunkaResponsiveDrawer :external-open-state="isLocationModalOpen" :on-click="() => {}" @update:is-modal-open="updateLocationModalOpen">
      <LocationMapDetail
        v-if="googleMapsUrl"
        :google-maps-url="googleMapsUrl"
        :is-loading="isStationsLoading"
        :is-map-loading="isMapLoading"
        :origin="origin"
        :static-map-url="staticMapUrl"
        :stations="stations"
        :venue-detail="experience.venueDetail"
      />
      <!-- 座標が無くマップを出せない場合のみ、開催地詳細だけを単体で表示する（Location セクションと同方針）。 -->
      <OrganismsPagesEnExperiencesDetailLocationSectionLocationDetail v-else-if="experience.venueDetail.length > 0" :venue-detail="experience.venueDetail" />
    </MoleculesWabunkaResponsiveDrawer>
  </div>
</template>

<script lang="ts" setup>
import type { EnExperienceViewModel } from '@/models/enExperience';
import { canShowOverviewDrawerLink } from '@/models/enExperience';
import type { WabunkaExperienceViewModel } from '@/models/wabunka/wabunkaExperience';
import { OptimizedImage } from '@/wabunka-src/common/components';
import DrawerLinkButton from '@/wabunka-src/common/features/Featured/components/DrawerLinkButton/DrawerLinkButton.vue';
import StyledRichText from '@/wabunka-src/common/features/Featured/components/StyledRichText/StyledRichText.vue';
import { sendGAWabunkaLocationDetailsOpenEvent } from '@/wabunka-src/common/utils/googleAnalytics/events/locationDetailsOpen';
import { buildGoogleMapsSearchUrl } from '@/wabunka-src/common/utils/googleMaps';
import { useNearestStationsQuery } from '@/wabunka-src/features/Experience/composables/query/useNearestStationsQuery';
import { useStaticMapUrlQuery } from '@/wabunka-src/features/Experience/composables/query/useStaticMapUrlQuery';
import LocationMapDetail from '@/wabunka-src/features/Experience/pages/ExperienceDetail/components/pure/LocationMapDetail/LocationMapDetail.vue';

interface Props {
  experienceCms: EnExperienceViewModel;
  experience: WabunkaExperienceViewModel;
  onDrawerLinkClick: (tabKey: string, label: string) => void;
}

const props = defineProps<Props>();

const overviewDrawerLink = computed(() => (canShowOverviewDrawerLink(props.experienceCms.overviewDrawerLink, props.experienceCms.drawerTabs) ? props.experienceCms.overviewDrawerLink : null));
const onClickOverviewDrawerLink = () => {
  if (!overviewDrawerLink.value) return;
  props.onDrawerLinkClick(overviewDrawerLink.value.tabKey, overviewDrawerLink.value.label);
};

const isLocationModalOpen = ref(false);

// Static マップ・最寄り駅は「Location Details」開封時にだけ遅延ロードする（直帰の多い詳細PV全件に課金しない）。
// Location セクション側と useFetch の key（path）を共有するため、どちらか一方で取得済みなら再フェッチされない。
const experienceId = computed(() => props.experience.id);
const { stations, status: stationsStatus, execute: executeStations } = useNearestStationsQuery(experienceId);
const { staticMapUrl, status: staticMapStatus, execute: executeStaticMap } = useStaticMapUrlQuery(experienceId);
const isStationsLoading = computed(() => stationsStatus.value === 'pending');
const isMapLoading = computed(() => staticMapStatus.value === 'pending');
const origin = computed(() => ({ latitude: props.experience.latitude ?? 0, longitude: props.experience.longitude ?? 0 }));

// 体験の緯度経度から Google Maps 検索 URL を生成する。座標が無い場合は null（リンク非表示）。
const googleMapsUrl = computed(() => buildGoogleMapsSearchUrl(props.experience.latitude, props.experience.longitude));

const updateLocationModalOpen = (newVal: boolean) => {
  isLocationModalOpen.value = newVal;
  // 開封時のみ計測＋遅延ロード（座標がある体験のみ）。
  // status が idle（同一ページの Location セクション側でも未取得）の場合だけ実行し、
  // 「開封数 ÷ 詳細PV ＝ Static 実課金数」の計測前提を崩さない（二重課金・二重計測防止）。
  if (newVal && experienceId.value && googleMapsUrl.value && staticMapStatus.value === 'idle') {
    sendGAWabunkaLocationDetailsOpenEvent({ experienceId: experienceId.value });
    void executeStations();
    void executeStaticMap();
  }
};

const openLocationDetailModal = () => {
  updateLocationModalOpen(true);
};

// 最適化は OptimizedImage 側で行うため、ここでは生の imgPath（無ければ null）を返す。
const destinationImageUrl = computed(() => {
  const destinationTag = props.experience.tags.find((experienceTag) => experienceTag.tag.tagGroupType === 'destination');
  return destinationTag && destinationTag.tag.imgPath.length > 0 ? destinationTag.tag.imgPath : null;
});
</script>
